import React, { FC } from 'react'
import { FormikProps } from 'formik'
import { CustomField, TypeSerial } from '../../../interfaces/Settings'
import { DatePicker, DateRangePicker, TimePicker } from "rsuite"
import clsx from 'clsx'
import moment from 'moment-timezone'
import { nanoid } from '@reduxjs/toolkit'
import Select from 'react-select'
import { useIntl } from 'react-intl'
import { customThousandSeparatorDot, getSubString, handleCaps, isValidUrl } from '../../../functions/general'
import InputPhone from '../../Input/InputPhone'
import InputAttachmentTermin from '../DetailTermin/InputAttachmentTermin'
import { AddLogFormValues } from '../Logs/AddLog'
import { useCustomeStore } from '../../../stores/CustomeStore'
import useUser from '../../../hooks/User'
import { CustomSelectMeetingOption, CustomSelectMerchantOption, CustomSelectPipelineSingleValue } from '../CustomComponentSelect/CustomSelect'
import { CustomSelectPipelineOption } from '../CustomComponentSelect/CustomSelect'
import VirtualizedSelect, { VirtualizedSelectOption, VirtualizedSelectOptionsType } from '../../Select/SelectVirtualized'
import { usePipelineStore } from '../../../stores/PipelineStore'
import { useMerchantStore } from '../../../stores/MerchantStore'
import { useUserStore } from '../../../stores/UserStore'
import { SimpleMerchant } from '../../../interfaces/Merchant'
import { User } from '../../../interfaces/User'
import { SimplePipeline } from '../../../interfaces/Pipeline'
import { getSelectedValueCustomFieldMeeting, getSelectedValueCustomFieldMerchant, getSelectedValueCustomFieldPipeline } from '../../../functions/helperCustomField'
import { SimpleMeeting } from '../../../interfaces/Meeting'
import { useMeetingStore } from '../../../stores/MeetingsStore'

interface Props {
    customFieldTermin: CustomField
    formik: FormikProps<AddLogFormValues>
    errors: any
    inputRefs: React.RefObject<(HTMLInputElement | HTMLTextAreaElement | null)[]>;
    MenuList?: (props: any) => JSX.Element
}

const RenderTerminCustomFieldLog: FC<Props> = ({ customFieldTermin, formik, errors, inputRefs, MenuList }) => {
    const intl = useIntl()
    const user = useUser()
    const { companyId } = useCustomeStore()

    const { simplePipelines, simplePipelineLoading } = usePipelineStore()
    const { simpleMerchants, simpleMerchantLoading, merchants } = useMerchantStore()
    const { simpleMeetings, simpleMeetingLoading } = useMeetingStore()
    const { users, userLoading } = useUserStore()

    // Helper function to safely check touched/errors
    const isTouched = (key: string): boolean => {
        return Boolean(
            errors.customFields?.[key]
        )
    }

    const getError = (key: string): string | undefined => {
        return errors.customFields?.[key];
    };

    const handleLinkValidation = (e: React.FocusEvent<HTMLInputElement>, key: string) => {
        const value = e.target.value;
        if (value && !isValidUrl(value)) {
            formik.setFieldError(`terminForm.customFields.${key}`, intl.formatMessage({ id: 'FORM.VALIDATION.INVALID_URL' }));
        }
    };

    const filteredCustomFields: Record<string, CustomField[keyof CustomField]> = Object.entries(customFieldTermin).reduce((acc, [key, field]) => {
        if (user?.data.company_id === companyId.maspionSquare && (field.name === 'No. Invoice' || field.name === 'Tanggal Create Nomor Invoice')) {
            return acc;
        }
        return { ...acc, [key]: field };
    }, {} as Record<string, CustomField[keyof CustomField]>);

    return (
        <div className="custom-fields-container">
            {Object.keys(filteredCustomFields).map((key, index) => {
                const field = filteredCustomFields[key]
                switch (field.type) {
                    case 'text':
                        return (
                            <div key={index}>
                                <label
                                    className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}
                                >
                                    {field.name}
                                </label>
                                <div className='input-group mb-3'>
                                    <input
                                        placeholder={field.name}
                                        {...formik.getFieldProps(`terminForm.customFields.${key}`)}
                                        className={clsx('form-control form-control-lg', {
                                            'is-invalid': isTouched(key) && getError(key),
                                        })}
                                        type='text'
                                        name={`terminForm.customFields.${key}`}
                                        autoComplete='off'
                                        style={{ zIndex: 0 }}
                                        required={field.required}
                                        onInput={(e) => handleCaps(e)}
                                        onBlur={() => {
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true);
                                        }}
                                        ref={(ref) => {
                                            if (inputRefs.current) {
                                                inputRefs.current[index] = ref
                                            }
                                        }}
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'paragraph':
                        return (
                            <div key={index}>
                                <label
                                    className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}
                                >
                                    {field.name}
                                </label>
                                <div className='input-group mb-3'>
                                    <textarea
                                        placeholder={field.name}
                                        {...formik.getFieldProps(`terminForm.customFields.${key}`)}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': isTouched(key) && getError(key) },
                                        )}
                                        style={{ zIndex: 0 }}
                                        required={field.required}
                                        name={`terminForm.customFields.${key}`}
                                        onInput={(e) => handleCaps(e)}
                                        onBlur={() => {
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true);
                                        }}
                                        ref={(ref) => {
                                            if (inputRefs?.current) {
                                                inputRefs.current[index] = ref
                                            }
                                        }}
                                    >
                                    </textarea>
                                </div>
                                {getError(key) && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'link':
                        return (
                            <div key={index}>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className="input-group mb-3">
                                    <input
                                        placeholder={field.name}
                                        {...formik.getFieldProps(`terminForm.customFields.${key}`)}
                                        className={clsx(
                                            'form-control form-control-lg txt-link',
                                            { 'is-invalid': isTouched(key) && getError(key) },
                                        )}
                                        type='text'
                                        name={`terminForm.customFields.${key}`}
                                        autoComplete='off'
                                        onBlur={(e) => {
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true);
                                            handleLinkValidation(e, key);
                                        }}
                                        style={{ zIndex: 0 }}
                                        required={field.required}
                                        ref={ref => {
                                            if (inputRefs.current) {
                                                inputRefs.current[index] = ref
                                            }
                                        }}
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'image':
                        return (
                            <div className='col-md-12 mb-3'>
                                {/* Attachments */}
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>{field.name}</span><br />
                                <small><i>*Format PNG, JPG, JPEG</i></small>
                                <InputAttachmentTermin key={key} keyString={key} formik={formik} isMulti={false} type="input" accept="image/png, image/jpeg, image/jpg" typeCustomField='addLog' />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'images':
                        return (
                            <div key={key} className='col-md-12 mb-3'>
                                {/* Attachments */}
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>{field.name}</span><br />
                                <small><i>*Format PNG, JPG, JPEG</i></small>
                                <InputAttachmentTermin key={key} keyString={key} formik={formik} isMulti={true} type="input" accept="image/png, image/jpeg, image/jpg" typeCustomField='addLog' />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'file':
                        return (
                            <div key={key} className="col-md-12 mb-3">
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </span><br />
                                <small><i>*Format PDF, XLS, XLSX, DOCX, PPT</i></small>
                                <InputAttachmentTermin
                                    key={key}
                                    keyString={key}
                                    formik={formik}
                                    isMulti={false}
                                    type="input"
                                    accept="application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    typeCustomField='addLog'
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'files':
                        return (
                            <div key={key} className="col-md-12 mb-3">
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </span><br />
                                <small><i>*Format PDF, XLS, XLSX, DOCX, PPT</i></small>
                                <InputAttachmentTermin
                                    key={key}
                                    keyString={key}
                                    formik={formik}
                                    isMulti={true}
                                    type="input"
                                    accept="application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    typeCustomField='addLog'
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'date':
                        return (
                            <div key={index} className='mb-3'>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className='input-group'>
                                    <DatePicker
                                        ranges={[]}
                                        oneTap
                                        editable={false}
                                        format='dd MMM yyyy'
                                        value={formik.values.terminForm?.customFields?.[key] ?
                                            new Date(formik.values.terminForm.customFields[key]) :
                                            null}
                                        onBlur={() => {
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true);
                                        }}
                                        style={{ width: '100%', zIndex: 0 }}
                                        onChange={(value: any) => {
                                            if (value)
                                                formik.setFieldValue(
                                                    `terminForm.customFields.${key}`,
                                                    moment(value).format('YYYY-MM-DD')
                                                )
                                            else
                                                formik.setFieldValue(`terminForm.customFields.${key}`, null)
                                        }}
                                        container={document.querySelector('#tambah-log-modal') as HTMLElement}
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'option': {
                        const options = field.value.map((option: string | TypeSerial) => ({
                            value: option,
                            label: option,
                        }))

                        return (
                            <div key={index}>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className='input-group mb-3'>
                                    <Select
                                        key={nanoid()}
                                        isClearable
                                        options={options}
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${field.name}`}
                                        value={
                                            options.find(
                                                (option) =>
                                                    option.value === formik.values.terminForm?.customFields?.[key]
                                            ) || null
                                        }
                                        onChange={(option) =>
                                            formik.setFieldValue(
                                                `terminForm.customFields.${key}`,
                                                option?.value || ''
                                            )
                                        }
                                        onBlur={() =>
                                            formik.values.terminForm?.customFields?.[key] === '' &&
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true)
                                        }
                                        className='w-100'
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    case 'multipleOption': {
                        const options = field.value.map((option: string | TypeSerial) => ({
                            value: option,
                            label: option,
                        }))

                        return (
                            <div key={index}>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className='input-group mb-3'>
                                    <Select
                                        key={nanoid()}
                                        isClearable
                                        options={options}
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${field.name}`}
                                        value={
                                            options?.filter(option => formik.values.terminForm?.customFields?.[key]?.includes(option.value))
                                        }
                                        onChange={(selectedOptions) =>
                                            formik.setFieldValue(
                                                `terminForm.customFields.${key}`,
                                                selectedOptions ? selectedOptions.map(option => option.value) : []
                                            )
                                        }
                                        onBlur={() =>
                                            formik.values.terminForm?.customFields?.[key] === '' &&
                                            formik.setFieldTouched(`terminForm.customFields.${key}`, true)
                                        }
                                        className='w-100'
                                        isMulti
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    case 'multiple':
                        return (
                            <div key={index}>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className='input-group mb-3'>
                                    <div className='form-check form-check-custom form-check-solid' style={{ display: 'contents' }}>
                                        {field.value.map((option: string | TypeSerial, optionIndex: number) => (
                                            <div style={{ display: 'flex', padding: '3px', overflowWrap: 'anywhere' }} key={optionIndex}>
                                                <input
                                                    className='form-check-input'
                                                    type='checkbox'
                                                    value={option as string}
                                                    name={`terminForm.customFields.${key}`}
                                                    onChange={(e) => {
                                                        const currentValues = formik.values.terminForm?.customFields?.[key] || []
                                                        let newValues
                                                        if (e.target.checked) {
                                                            newValues = [...currentValues, e.target.value]
                                                        } else {
                                                            newValues = currentValues.filter((value: string) => value !== e.target.value)
                                                        }
                                                        formik.setFieldValue(`terminForm.customFields.${key}`, newValues)
                                                    }}
                                                    onBlur={() => {
                                                        formik.setFieldTouched(`terminForm.customFields.${key}`, true);
                                                    }}
                                                    checked={formik.values.terminForm?.customFields?.[key]?.includes(option as string) || false}
                                                />
                                                <label className='form-check-label me-2'>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'phone':
                        return (
                            <div key={index} className='mb-3'>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <InputPhone
                                    keyString={key}
                                    formik={formik}
                                    type="input"
                                    typeField='terminForm'
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )


                    case 'number':
                        return (
                            <div key={index} className='mb-3'>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <input
                                    type="text"
                                    name={`terminForm.customFields.${key}`}
                                    value={formik.values.terminForm?.customFields?.[key] || ''}
                                    onBlur={formik.handleBlur}
                                    onChange={(e) => {
                                        const cleanValue = customThousandSeparatorDot(e.target.value);
                                        formik.setFieldValue(`terminForm.customFields.${key}`, cleanValue);
                                    }}
                                    className={clsx('form-control form-control-lg', {
                                        'is-invalid': isTouched(key) && getError(key),
                                    })}
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )
                    case 'dateTimeRange':
                        const formatDateRange = (value: any) => {
                            if (Array.isArray(value)) {
                                return value.map((item: any) => new Date(item))
                            }
                            return value
                        }
                        return (
                            <div key={index} className='mb-3'>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </label>
                                <div className='input-group'>
                                    <DateRangePicker
                                        format="dd MMM yyyy HH:mm"
                                        value={formatDateRange(formik.values.terminForm?.customFields?.[key]) || null}
                                        defaultValue={null}
                                        style={{ width: "100%", zIndex: 0 }}
                                        container={document.querySelector('#tambah-log-modal') as HTMLElement}
                                        onChange={(value: any) => {
                                            formik.setFieldValue(`terminForm.customFields.${key}`, value)
                                        }}
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'time':
                        const formatDate = (value: any) => {
                            if (!value) return null;
                            if (typeof value === 'string') {
                                return new Date(value);
                            }
                            if (value instanceof Date) {
                                return value;
                            }

                            return null;
                        }

                        return (
                            <div key={index} className='mb-3'>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required ? 'required' : ''}`}>
                                    {field.name}
                                </label>
                                <div className='input-group'>
                                    <TimePicker
                                        format="HH:mm"
                                        value={formatDate(formik.values.terminForm?.customFields?.[key])}
                                        defaultValue={null}
                                        style={{ width: "100%", zIndex: 0 }}
                                        container={document.querySelector('#tambah-log-modal') as HTMLElement}
                                        onChange={(value: any) => {
                                            formik.setFieldValue(`terminForm.customFields.${key}`, value ? value.toISOString() : null)
                                        }}
                                    />
                                </div>
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'user':
                        const optionsUser = users?.map((option: User) => ({
                            value: option.id,
                            label: option.name
                        }))

                        return (
                            <>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required ? 'required' : ''}`}>{field.name}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        options={optionsUser as VirtualizedSelectOptionsType[]}
                                        isLoading={userLoading}
                                        isDisabled={userLoading}
                                        className="w-100"
                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} User`}
                                        value={optionsUser?.filter(option => formik.values.terminForm?.customFields?.[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                        onChange={(selectedOptions) =>
                                            formik.setFieldValue(
                                                `terminForm.customFields.${key}`,
                                                selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                            )
                                        }
                                        onBlur={() => {
                                            if (formik.values.terminForm?.customFields?.[key] === "") {
                                                formik.setFieldTouched(key, true);
                                            }
                                        }}
                                        components={{ MenuList }}
                                        isMulti
                                    />
                                </div>
                                {
                                    getError(key) &&
                                    (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{getError(key)}</span>
                                        </div>
                                    )
                                }
                            </>
                        )
                    case 'pipeline':
                        const filteredPipelines = getSelectedValueCustomFieldPipeline(filteredCustomFields, simplePipelines, key)

                        const optionsSimplePipeline = filteredPipelines.map((option: SimplePipeline) => ({
                            ...option,
                            value: option.id,
                            label: option.name!
                        }))

                        return (
                            <>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required ? 'required' : ''}`}>{field.name}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        options={optionsSimplePipeline as VirtualizedSelectOptionsType[]}
                                        isLoading={simplePipelineLoading}
                                        isDisabled={simplePipelineLoading}
                                        className="w-100"
                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Pipeline`}
                                        value={optionsSimplePipeline?.find(option => option.value === formik.values.terminForm?.customFields?.[key]) || null}
                                        onChange={(selectedOption) => {
                                            formik.setFieldValue(`terminForm.customFields.${key}`, selectedOption ? selectedOption.value : null)
                                        }
                                        }
                                        onBlur={() => {
                                            if (formik.values.terminForm?.customFields?.[key] === "") {
                                                formik.setFieldTouched(key, true);
                                            }
                                        }}
                                        isMulti={false}
                                        components={{
                                            Option: CustomSelectPipelineOption,
                                            SingleValue: CustomSelectPipelineSingleValue
                                        }}
                                    />
                                </div>
                                {
                                    getError(key) &&
                                    (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{getError(key)}</span>
                                        </div>
                                    )
                                }
                            </>
                        )
                    case 'merchant':
                        const filteredMerchants = getSelectedValueCustomFieldMerchant(filteredCustomFields, simpleMerchants, key)
                        const optionsSimpleMerchant = filteredMerchants.map((option: SimpleMerchant) => ({
                            value: option.id,
                            label: `${option.name} / ${option.phone}`
                        }))

                        return (
                            <>
                                <label className={`form-label fs-6 fw-bolder text-dark ${field.required ? 'required' : ''}`}>{field.name}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        // title="Custom Field Merchant"
                                        key={
                                            window.location.pathname === '/chat' ? nanoid() : ''
                                        }
                                        options={optionsSimpleMerchant as VirtualizedSelectOptionsType[]}
                                        // options={dummySingleOptions}
                                        // options={dummyGroupOptions}
                                        isLoading={simpleMerchantLoading}
                                        isDisabled={simpleMerchantLoading}
                                        className="w-100"
                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Merchant`}
                                        value={optionsSimpleMerchant?.filter(option => formik.values.terminForm?.customFields?.[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                        onChange={(selectedOptions) =>
                                            formik.setFieldValue(
                                                `terminForm.customFields.${key}`,
                                                selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                            )
                                        }
                                        onBlur={() => {
                                            if (formik.values.terminForm?.customFields?.[key] === "") {
                                                formik.setFieldTouched(key, true);
                                            }
                                        }}
                                        components={{
                                            Option: (props: any) => CustomSelectMerchantOption({
                                                merchants,
                                                option: props.option,
                                                user,
                                                companyId
                                            })
                                        }}
                                        typeMultiValue='merchant'
                                        isMultiValue
                                        isMulti
                                    />
                                </div>
                                {
                                    getError(key) &&
                                    (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{getError(key)}</span>
                                        </div>
                                    )
                                }
                            </>
                        )
                    case 'meeting':
                        const filteredMeetings = getSelectedValueCustomFieldMeeting(filteredCustomFields, simpleMeetings, key)
                        const optionSimpleMeeting = filteredMeetings.map((option: SimpleMeeting) => ({
                            ...option,
                            value: option.id,
                            label: `${option.name!} / ${moment(option.dateMeet).format("DD MMM YYYY")} / ${option.meetingStatus} / ${getSubString(option.id)}`
                        }))
                        return (
                            <>
                                <label key={key} className={`form-label fs-6 fw-bolder text-dark ${field.required ? 'required' : ''}`}>{field.name}</label>
                                <div className="input-group mb-3">
                                    <VirtualizedSelect
                                        options={optionSimpleMeeting as VirtualizedSelectOptionsType[]}
                                        isLoading={simpleMeetingLoading}
                                        isDisabled={simpleMeetingLoading}
                                        className="w-100"
                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} Meeting`}
                                        value={optionSimpleMeeting?.filter(option => formik.values.terminForm?.customFields?.[key]?.includes(option.value)) as VirtualizedSelectOption[]}
                                        onChange={(selectedOptions) =>
                                            formik.setFieldValue(
                                                `terminForm.customFields.${key}`,
                                                selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                            )
                                        }
                                        onBlur={() => {
                                            if (formik.values.terminForm?.customFields?.[key] === "") {
                                                formik.setFieldTouched(key, true);
                                            }
                                        }}
                                        isMulti
                                        components={{
                                            Option: CustomSelectMeetingOption,
                                        }}
                                        isMultiValue
                                        typeMultiValue="meeting"
                                    />
                                </div>
                                {
                                    getError(key) &&
                                    (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{getError(key)}</span>
                                        </div>
                                    )
                                }
                            </>
                        )
                    case 'video':
                        return (
                            <div key={key} className="col-md-12 mb-3">
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </span><br />
                                <small><i>*Format MP4, MOV, WEBM, MKV</i></small>
                                <InputAttachmentTermin
                                    key={key}
                                    keyString={key}
                                    formik={formik}
                                    isMulti={false}
                                    type="input"
                                    accept="video/mp4, video/mov, video/webm, video/mkv"
                                    typeCustomField='addLog'
                                    isVideo={true}
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )

                    case 'videos':
                        return (
                            <div key={key} className="col-md-12 mb-3">
                                <span className={`form-label fs-6 fw-bolder text-dark ${field.required && 'required'}`}>
                                    {field.name}
                                </span><br />
                                <small><i>*Format MP4, MOV, WEBM, MKV</i></small>
                                <InputAttachmentTermin
                                    key={key}
                                    keyString={key}
                                    formik={formik}
                                    isMulti={true}
                                    type="input"
                                    accept="video/mp4, video/mov, video/webm, video/mkv"
                                    typeCustomField='addLog'
                                    isVideo={true}
                                />
                                {getError(key) && (
                                    <div className='text-danger fv-plugins-message-container'>
                                        <span role='alert' className='text-danger'>
                                            {getError(key)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )
                    default:
                        return null
                }
            })}
        </div>
    )
}

export default RenderTerminCustomFieldLog